import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ShipmentStatusError1Preview from '../../../../../../images/previews/shipment-status-error-1.svg';
import ShipmentStatusError2Preview from '../../../../../../images/previews/shipment-status-error-2.svg';
import ShipmentStatusError3Preview from '../../../../../../images/previews/shipment-status-error-3.svg';
import ShipmentStatusError4Preview from '../../../../../../images/previews/shipment-status-error-4.svg';
import UnfulfilledItemsPreview from '../../../../../../images/previews/unfulfilled-items-preview.svg';
import { FormField } from "../../../../../../components/form-field/form-field";
import { TRACKING_PORTAL_TRANSLATION_KEYS } from "../../constants";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import TranslationTextarea from "../../../../../../translation/translation-textarea/translation-textarea";
import TranslationRichTextEditor from "../../../../../../translation/translation-rich-text-editor/translation-rich-text-editor";
import { EDITOR_VARIANT } from "../../../../../../components/rich-text-editor/rich-text-editor";

const WarningAndMessagesEditor: React.FC = observer(() => {
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const {
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations
    } = useTranslation();

    const hasChanges = () => {
        return hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >
                <ListLayout>
                    <ControlGroupWithPreview hasTopBorder={false} preview={<img src={ShipmentStatusError1Preview} alt="" />}>
                        <ListLayout>
                            <FormField
                                label='Header'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.ERROR} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_ERROR} />
                            </FormField>
                            <FormField label="More Information">
                                <TranslationRichTextEditor 
                                    translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_ERR_GENERAL_ERROR_HYPERLINK} 
                                    variant={EDITOR_VARIANT.HYPERLINK_ONLY}
                                />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusError2Preview} alt="" />}>
                        <ListLayout>
                            <FormField
                                label='Header'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.NO_RESULTS} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.SHIPMENT_NOT_FOUND_DESCRIPTION} />
                            </FormField>

                            <FormField label="More Information">
                                <TranslationRichTextEditor 
                                    translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_ERR_SHIPMENT_NOT_FOUND_HYPERLINK} 
                                    variant={EDITOR_VARIANT.HYPERLINK_ONLY}
                                />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusError3Preview} alt="" />}>
                        <ListLayout>
                            <FormField
                                label='Header'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.NO_TRACKING_NUMBER} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.NO_TRACKING_NUMBER_DESCRIPTION} />
                            </FormField>

                            <FormField label="More Information">
                                <TranslationRichTextEditor 
                                    translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_ERR_TRACKING_NOT_FOUND_HYPERLINK} 
                                    variant={EDITOR_VARIANT.HYPERLINK_ONLY}
                                />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusError4Preview} alt="" />}>
                        <ListLayout>
                            <FormField
                                label='Header'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.ORDER_CANCELLED} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.ORDER_CANCELLED_DESCRIPTION} />
                            </FormField>

                            <FormField label="More Information">
                                <TranslationRichTextEditor 
                                    translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_ERR_ORDER_CANCELLED_HYPERLINK} 
                                    variant={EDITOR_VARIANT.HYPERLINK_ONLY}  
                                />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={UnfulfilledItemsPreview} alt="" />}>
                        <ListLayout>
                            <FormField
                                label='Unfulfilled Items Header'
                                instructions="It is recommended to keep headers between 1-4 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.UNFULFILLED_NOTIFICATION_HEADER} />
                            </FormField>

                            <FormField label="Unfulfilled Items Message">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.UNFULFILLED_NOTIFICATION_DESCRIPTION} />
                            </FormField>

                            <FormField label="More Information">
                                <TranslationRichTextEditor 
                                    translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.UNFULFILLED_NOTIFICATION_HYPERLINK} 
                                    variant={EDITOR_VARIANT.HYPERLINK_ONLY}  
                                />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </>
    );
});

export default WarningAndMessagesEditor;